import { formatCount, formatPct } from "@nexus/ui";
import { WidgetSummary, WidgetFooter, WidgetIssueState } from "@nexus/widget-sdk";
import {
  RankingList,
  RANKING_ROW as ROW,
  RANKING_METRIC as METRIC_COL,
  RANKING_VIZ as VIZ_COL,
  RANKING_META as META_ROW,
} from "@nexus/widget-kit";
import { memo, useMemo } from "react";
import {
  defineLabels,
  firstError,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  StatusChip,
} from "@nexus/widget-sdk";
import type { StatusState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type {
  ResponsePerEngineConfig,
  ResponsePerEngineData,
  ResponsePerEngineEntry,
} from "./types";

export type {
  ResponsePerEngineConfig,
  ResponsePerEngineData,
  ResponsePerEngineEntry,
} from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No responses" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "Widen the time range to see engine activity.",
  },
  { key: "summaryLabel", name: "Summary caption", default: "responses" },
  {
    key: "summaryDescription",
    name: "Summary description",
    default: "Ranked by actionable infringements",
  },
  { key: "engineHeading", name: "Engine column heading", default: "Engine" },
  {
    key: "confirmedHeading",
    name: "Confirmed column heading",
    default: "Confirmed",
  },
  {
    key: "outcomesHeading",
    name: "Outcomes column heading",
    default: "Outcomes",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "response-per-engine",
  name: "Response per Engine",
  description: "Response outcomes grouped by response engine.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    engines: {
      source: "response-per-engine",
      contract: { name: "response-per-engine", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: ResponsePerEngineConfig = {
  title: "Response per Engine",
  maxEngines: 10,
};

const EMPTY_ENGINES: ResponsePerEngineEntry[] = [];

// ── Helpers ──────────────────────────────────────────────────────────────────

function totalResponses(entry: ResponsePerEngineEntry): number {
  return entry.confirmed_count + entry.pending_count + entry.failed_count + entry.cancelled_count;
}

// Confirmed rate thresholds mirror the KPI widget: error < 75%, warn < 90%, ok otherwise.
function getStatus(confirmedRate: number): StatusState {
  if (confirmedRate < 0.75) return "error";
  if (confirmedRate < 0.9) return "warn";
  return "ok";
}

// Single tone table: drives the meta line and the response bar.
// Nominal stays muted — the row only takes color when something is wrong.
const TONE: Record<StatusState, string> = {
  ok: "text-muted",
  warn: "text-status-warn-text",
  error: "text-status-error-text",
  neutral: "text-muted",
};

// ── Sub-components ────────────────────────────────────────────────────────────

function Separator() {
  return <span className="h-2.5 w-px shrink-0 bg-border/70" />;
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className={METRIC_COL}>
      <div className="text-xl font-semibold leading-none tracking-display tabular-nums text-primary">
        {value}
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

// Stacked composition of the responses: confirmed / pending / failed / cancelled.
// Occupies the same slot as the detection sparkline, at the same height.
function ResponseBar({ entry }: { entry: ResponsePerEngineEntry }) {
  const total = totalResponses(entry);

  const segments =
    total === 0
      ? []
      : [
          {
            key: "confirmed",
            width: (entry.confirmed_count / total) * 100,
            color: "var(--status-ok)",
          },
          {
            key: "pending",
            width: (entry.pending_count / total) * 100,
            color: "var(--status-warn)",
          },
          {
            key: "failed",
            width: (entry.failed_count / total) * 100,
            color: "var(--status-error)",
          },
          {
            key: "cancelled",
            width: (entry.cancelled_count / total) * 100,
            color: "var(--status-neutral)",
          },
        ].filter((segment) => segment.width > 0);

  return (
    <div className="flex h-full items-center">
      <div
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-border/40"
        role="img"
        aria-label={`Confirmed ${formatPct(entry.confirmed_rate)}, pending ${Math.round(entry.pending_rate * 100)}%, failed ${Math.round(entry.failed_rate * 100)}%`}
      >
        {segments.map((segment) => (
          <span
            key={segment.key}
            className="h-full shrink-0"
            style={{
              width: `${segment.width}%`,
              backgroundColor: segment.color,
            }}
          />
        ))}
      </div>
    </div>
  );
}

const EngineRow = memo(function EngineRow({ entry }: { entry: ResponsePerEngineEntry }) {
  const status = getStatus(entry.confirmed_rate);
  const total = totalResponses(entry);

  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="min-w-0 truncate text-sm font-semibold leading-5 text-primary"
            title={entry.engine_label}
          >
            {entry.engine_label}
          </span>
          {status !== "ok" && <StatusChip status={status} />}
        </div>

        <div className={META_ROW}>
          <span className="shrink-0 tabular-nums">
            {formatCount(total)} responses · {formatCount(entry.total_actionable_infringements)}{" "}
            actionable
          </span>
          {entry.pending_count > 0 && (
            <>
              <Separator />
              <span className="shrink-0 tabular-nums text-status-warn-text">
                {formatCount(entry.pending_count)} pending
              </span>
            </>
          )}
          {entry.failed_count > 0 && (
            <>
              <Separator />
              <span className="shrink-0 tabular-nums text-status-error-text">
                {formatCount(entry.failed_count)} failed
              </span>
            </>
          )}
        </div>
      </div>

      <Metric value={formatPct(entry.confirmed_rate)} label="confirmed" />

      <div className={`${VIZ_COL} ${TONE[status]}`}>
        <ResponseBar entry={entry} />
      </div>
    </div>
  );
});

function RowSkeleton() {
  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3 w-32 rounded skeleton" />
        <div className="h-2.5 w-40 rounded skeleton" />
      </div>
      <div className={METRIC_COL}>
        <div className="ml-auto h-5 w-12 rounded skeleton" />
      </div>
      <div className={`${VIZ_COL} rounded skeleton`} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function ResponsePerEngineComponent({ data, config }: WidgetProps<ResponsePerEngineConfig>) {
  const error = firstError(data, ["engines"]);
  const resolved = unwrap<ResponsePerEngineData>(data.engines);
  const isLoading = data.engines?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Response per Engine";
  const labels = resolveLabels(LABELS, config);
  const maxEngines = config.maxEngines ?? defaultConfig.maxEngines ?? 10;

  const allEngines = resolved?.engines ?? EMPTY_ENGINES;

  const { engines, grandTotal, hiddenCount } = useMemo(() => {
    const sorted = [...allEngines].sort(
      (a, b) => b.total_actionable_infringements - a.total_actionable_infringements,
    );
    const top = sorted.slice(0, maxEngines);
    const total = allEngines.reduce((sum, e) => sum + totalResponses(e), 0);
    return {
      engines: top,
      grandTotal: total,
      hiddenCount: allEngines.length - top.length,
    };
  }, [allEngines, maxEngines]);

  if (error) {
    console.error("[ResponsePerEngineWidget] Failed to load data:", error);
    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  const hasData = engines.length > 0;

  if (!hasData && isLoading) {
    return (
      <Widget title={title}>
        <div className="h-full overflow-hidden">
          {Array.from({ length: Math.min(maxEngines, 5) }, (_, index) => (
            <RowSkeleton key={index} />
          ))}
        </div>
      </Widget>
    );
  }

  if (!hasData) {
    return (
      <Widget title={title}>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget title={title} actions={isLoading ? <UpdatingPill /> : undefined}>
      <div className="@container flex h-full min-h-0 flex-col">
        <WidgetSummary
          value={formatCount(grandTotal)}
          exactValue={grandTotal.toLocaleString()}
          label={labels.summaryLabel}
          description={labels.summaryDescription}
        />
        <RankingList
          identity={labels.engineHeading}
          metric={labels.confirmedHeading}
          visualization={labels.outcomesHeading}
        >
          {engines.map((entry) => (
            <EngineRow key={entry.engine_id} entry={entry} />
          ))}
        </RankingList>
        <WidgetFooter>
          <span>
            {engines.length} of {allEngines.length} engines
          </span>
          {hiddenCount > 0 ? <span>· {hiddenCount} not shown</span> : null}
        </WidgetFooter>
      </div>
    </Widget>
  );
}

export const Component = ResponsePerEngineComponent;

const widgetModule: WidgetModule<ResponsePerEngineConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
